import MUITextField from '@mui/material/TextField';
import React from 'react';

import { PasswordTextField } from '@/auth/components/PasswordTextField';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Link from '@/components/Link';
import { EmailDeliveryHelp } from '@/componentsv2/EmailDeliveryHelp';
import { OTP_METHODS, OtpMethod } from '@/core/api';

import type { MfaState } from '../hooks';
import { useStyles } from '../styles';

interface LoginFormProps {
  loginEmail: string;
  setLoginEmail: (email: string) => void;
  loginPassword: string;
  setLoginPassword: (password: string) => void;
  loginError: string | null;
  isLoggingIn: boolean;
  onLogin: () => void;
  onBack: () => void;
  // MFA props
  mfa: MfaState;
  otpCode: string;
  setOtpCode: (code: string) => void;
  onMfaVerify: () => void;
  onMfaCancel: () => void;
  onMfaResend: () => void;
  mfaResendSuccess?: boolean;
}

export function LoginForm({
  loginEmail,
  setLoginEmail,
  loginPassword,
  setLoginPassword,
  loginError,
  isLoggingIn,
  onLogin,
  onBack,
  // MFA props
  mfa,
  otpCode,
  setOtpCode,
  onMfaVerify,
  onMfaCancel,
  onMfaResend,
  mfaResendSuccess,
}: LoginFormProps): JSX.Element {
  const s = useStyles();
  const isFormValid = loginEmail && loginPassword;
  const isOtpValid = otpCode.length >= 6;

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid && !isLoggingIn) {
      onLogin();
    }
  };

  const handleMfaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isOtpValid && !isLoggingIn) {
      onMfaVerify();
    }
  };

  // Get display text for OTP method
  const getMethodDisplayText = (method: OtpMethod | null): string => {
    switch (method) {
      case OTP_METHODS.SMS:
        return 'SMS';
      case OTP_METHODS.EMAIL:
        return 'email';
      case OTP_METHODS.APP:
        return 'authenticator app';
      default:
        return 'verification';
    }
  };

  // MFA verification form
  if (mfa.required) {
    const methodText = getMethodDisplayText(mfa.method);
    const showResend = mfa.method !== OTP_METHODS.APP;

    return (
      <form onSubmit={handleMfaSubmit}>
        <div className={s.formContent}>
          <p
            className={s.phoneInstructions}
            style={{ fontSize: 16, marginTop: 24, marginBottom: 18 }}
          >
            Enter the {methodText} code
            {mfa.maskedAddress && mfa.method !== OTP_METHODS.APP && (
              <>
                {' '}
                sent to <span style={{ fontWeight: 500 }}>{mfa.maskedAddress}</span>
              </>
            )}
          </p>

          {loginError && (
            <Alert type="error" center style={{ fontSize: 14, padding: 12, marginBottom: 10 }}>
              {loginError}
            </Alert>
          )}

          <div style={{ maxWidth: 380, margin: '0 auto' }}>
            <MUITextField
              id="otpCode"
              variant="outlined"
              fullWidth
              label="Verification Code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              inputProps={{
                maxLength: 6,
                'aria-label': 'Enter your 6-digit multi-factor authentication code',
              }}
              placeholder="Enter 6-digit code"
            />
          </div>

          {showResend && (
            <div className={s.resendLink}>
              {mfaResendSuccess ? (
                <span style={{ color: '#059669', fontSize: 14 }}>Code resent successfully</span>
              ) : (
                <button
                  type="button"
                  className={s.resendLinkText}
                  onClick={onMfaResend}
                  disabled={isLoggingIn}
                >
                  Resend code
                </button>
              )}
              {mfa.method === OTP_METHODS.EMAIL && (
                <div>
                  <EmailDeliveryHelp align="center" className="mt-8" />
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, textAlign: 'center', fontSize: 12, color: '#6B7280' }}>
            If you have issues, contact hello@onfrontiers.com
          </div>
        </div>

        <div className={s.footer}>
          <Button
            variant="text"
            classes={{ root: s.backButton }}
            onClick={onMfaCancel}
            disabled={isLoggingIn}
          >
            Cancel
          </Button>
          <Button
            color="teal"
            size="normal"
            type="submit"
            disabled={!isOtpValid || isLoggingIn}
            classes={{ root: s.continueButton }}
          >
            {isLoggingIn ? 'Verifying...' : 'Verify'}
          </Button>
        </div>
      </form>
    );
  }

  // Standard email/password form
  return (
    <form onSubmit={handleLoginSubmit}>
      <div className={s.formContent}>
        <p
          className={s.phoneInstructions}
          style={{ fontSize: 16, marginTop: 24, marginBottom: 18 }}
        >
          Log in to your account to continue with your consultation request.
        </p>

        {loginError && (
          <Alert type="error" center style={{ fontSize: 14, padding: 12, marginBottom: 10 }}>
            {loginError}
          </Alert>
        )}

        <div style={{ maxWidth: 380, margin: '0 auto' }}>
          <MUITextField
            id="loginEmail"
            variant="outlined"
            fullWidth
            type="email"
            label="Email address"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            required
            style={{ marginBottom: 10 }}
          />

          <PasswordTextField
            id="loginPassword"
            endAdornmentId="loginPasswordEye"
            variant="outlined"
            fullWidth
            label="Password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
          />
        </div>

        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <Link href="/password_reset" newTab>
            Forgot your password?
          </Link>
        </div>

        <div className={s.loginLink}>
          <button type="button" className={s.loginLinkText} onClick={onBack}>
            Use phone instead →
          </button>
        </div>
      </div>

      <div className={s.footer}>
        <Button
          variant="text"
          classes={{ root: s.backButton }}
          onClick={onBack}
          disabled={isLoggingIn}
        >
          Back
        </Button>
        <Button
          color="teal"
          size="normal"
          type="submit"
          disabled={!isFormValid || isLoggingIn}
          classes={{ root: s.continueButton }}
        >
          {isLoggingIn ? 'Logging in...' : 'Log In'}
        </Button>
      </div>
    </form>
  );
}

export default LoginForm;
